import { useQuery } from '@tanstack/react-query';
import {
  Title,
  Text,
  Grid,
  Card,
  Badge,
  Metric,
  BarList,
} from '@tremor/react';
import { KPICard } from '@/components/KPICard';
import { SatisfacaoCard } from '@/components/SatisfacaoCard';
import { RetencaoCurvaCard } from '@/components/RetencaoCurvaCard';
import { RetencaoCohortsMensaisCard } from '@/components/RetencaoCohortsMensaisCard';
import { RecompraCard } from '@/components/RecompraCard';
import { EmailEngagementSection } from '@/components/EmailEngagementSection';
import { getRetencao, extractErrorMessage } from '@/lib/api';

interface FunnelData {
  signups: number | null;
  activated: number | null;
  activation_rate: number | null;
  repeat: number | null;
  paid: number | null;
}

interface NorthStarData {
  label: string;
  value: number | null;
  n: number | null;
  base: number | null;
}

interface LiftRow {
  behavior: string;
  n: number | null;
  p_with: number | null;
  p_without: number | null;
  lift: number | null;
}

interface RetentionAction {
  name: string;
  status: string;
  reach: number | null;
  impact_label: string;
  impact: number | null;
}

interface EditStep {
  step: string;
  n: number | null;
}

interface EditFunnel {
  processos_em_revisao: number | null;
  steps: EditStep[];
  editores: number | null;
  editou_e_abandonou: number | null;
  taxa_abandono_edicao: number | null;
}

interface RetencaoData {
  funnel: FunnelData;
  north_star: NorthStarData;
  lift: LiftRow[];
  retention_actions: RetentionAction[];
  edit_funnel: EditFunnel;
}

function fmtInt(n: number | null | undefined): string {
  if (n === null || n === undefined) return '—';
  return new Intl.NumberFormat('pt-BR').format(n);
}

function fmtPct(n: number | null | undefined, decimals = 1): string {
  if (n === null || n === undefined) return '—';
  return `${(n * 100).toFixed(decimals)}%`;
}

function fmtLift(n: number | null | undefined): string {
  if (n === null || n === undefined) return '—';
  return `${n.toFixed(1)}×`;
}

function statusColor(status: string): 'emerald' | 'amber' | 'slate' {
  if (status === 'ativa') return 'emerald';
  if (status === 'pausada') return 'amber';
  return 'slate';
}

export function RetencaoPage() {
  const { data, isLoading, error } = useQuery<RetencaoData>({
    queryKey: ['retencao'],
    queryFn: async () => (await getRetencao()).data,
    refetchInterval: 60_000,
  });

  const errMsg = error
    ? extractErrorMessage(error, 'erro ao carregar retenção')
    : null;

  const f = data?.funnel;
  const ns = data?.north_star;
  const ef = data?.edit_funnel;

  const liftItems = (data?.lift ?? [])
    .filter((l) => l.lift !== null && l.lift !== undefined)
    .map((l) => ({
      name: `${l.behavior} (n=${fmtInt(l.n)})`,
      value: l.lift as number,
    }));

  const stepItems = (ef?.steps ?? []).map((s) => ({
    name: s.step,
    value: s.n ?? 0,
  }));

  return (
    <div className="space-y-6">
      <header className="space-y-1">
        <div className="flex items-center justify-between">
          <Title>Retenção</Title>
          {data && (
            <Badge color="emerald" size="sm">
              atualizado {new Date().toLocaleTimeString('pt-BR')}
            </Badge>
          )}
        </div>
        <Text>
          Funil de ativação e retenção. O vazamento nº 1 é a ATIVAÇÃO — a maioria
          cadastra e não roda o primeiro cálculo.
        </Text>
      </header>

      {/* Linha 1: Funil */}
      <section className="space-y-2">
        <Text className="text-xs uppercase tracking-wide text-slate-400">
          Funil de ativação
        </Text>
        <Grid numItems={1} numItemsSm={2} numItemsLg={4} className="gap-4">
          <KPICard
            label="Signups"
            value={isLoading ? null : fmtInt(f?.signups)}
            loading={isLoading}
            error={errMsg}
            hint="cadastros totais"
          />
          <KPICard
            label="Ativação"
            value={isLoading ? null : fmtPct(f?.activation_rate)}
            loading={isLoading}
            error={errMsg}
            hint={`${fmtInt(f?.activated)} rodaram o 1º cálculo · 🔴 vazamento nº 1`}
          />
          <KPICard
            label="2º caso"
            value={isLoading ? null : fmtInt(f?.repeat)}
            loading={isLoading}
            error={errMsg}
            hint="voltaram p/ um segundo caso"
          />
          <KPICard
            label="Pagantes"
            value={isLoading ? null : fmtInt(f?.paid)}
            loading={isLoading}
            error={errMsg}
            hint="converteram em pagamento"
          />
        </Grid>
      </section>

      {/* Linha 2: North star hero */}
      <section className="space-y-2">
        <Text className="text-xs uppercase tracking-wide text-slate-400">
          Evento-norte
        </Text>
        <Card className="border border-blue-700/40 bg-blue-900/10">
          {isLoading && <Text>carregando…</Text>}
          {!isLoading && errMsg && (
            <div className="text-sm text-red-400" role="alert">
              {errMsg}
            </div>
          )}
          {!isLoading && !errMsg && (
            <div className="space-y-1">
              <Text className="text-blue-200">{ns?.label ?? 'evento-norte'}</Text>
              <Metric className="text-blue-300">{fmtPct(ns?.value)}</Metric>
              <Text className="text-xs text-slate-400">
                {fmtInt(ns?.n)} de {fmtInt(ns?.base)} ativados
              </Text>
              <Text className="pt-1 text-sm text-blue-100/80">
                Quem entra na revisão paga ~9× mais.
              </Text>
            </div>
          )}
        </Card>
      </section>

      {/* Curva de retorno (download→upload) — retenção ancorada no VALOR */}
      <RetencaoCurvaCard />

      {/* Cohorts mensais de signup — safras recentes × antigas, semana a semana */}
      <RetencaoCohortsMensaisCard />

      {/* Recompra — retenção monetária (2ª compra Stripe desde a 1ª) */}
      <RecompraCard />

      {/* Satisfação com o cálculo (CSAT do polegar) — sinal de qualidade/retenção */}
      <SatisfacaoCard />

      {/* Linha 3: Lift por comportamento */}
      <section className="space-y-2">
        <Text className="text-xs uppercase tracking-wide text-slate-400">
          Lift de pagamento por comportamento
        </Text>
        <Card>
          {isLoading && <Text>carregando…</Text>}
          {!isLoading && errMsg && (
            <div className="text-sm text-red-400" role="alert">
              {errMsg}
            </div>
          )}
          {!isLoading && !errMsg && liftItems.length > 0 && (
            <>
              <div className="mb-2 flex items-center justify-between">
                <Text className="text-xs uppercase tracking-wide text-slate-400">
                  comportamento
                </Text>
                <Text className="text-xs uppercase tracking-wide text-slate-400">
                  lift (× pagar)
                </Text>
              </div>
              <BarList
                data={liftItems}
                valueFormatter={(v: number) => `${v.toFixed(1)}×`}
                color="blue"
              />
            </>
          )}
          {!isLoading && !errMsg && liftItems.length === 0 && (
            <Text className="text-sm text-slate-400">sem dados de lift</Text>
          )}
        </Card>
      </section>

      {/* Linha 4: Ações de retenção */}
      <section className="space-y-2">
        <Text className="text-xs uppercase tracking-wide text-slate-400">
          Ações de retenção
        </Text>
        <Card>
          {isLoading && <Text>carregando…</Text>}
          {!isLoading && errMsg && (
            <div className="text-sm text-red-400" role="alert">
              {errMsg}
            </div>
          )}
          {!isLoading && !errMsg && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-800 text-left text-xs uppercase tracking-wide text-slate-400">
                    <th className="py-2 pr-4 font-medium">Ação</th>
                    <th className="py-2 pr-4 font-medium">Status</th>
                    <th className="py-2 pr-4 font-medium">Alcance</th>
                    <th className="py-2 pr-4 font-medium">Impacto</th>
                  </tr>
                </thead>
                <tbody>
                  {(data?.retention_actions ?? []).map((a) => (
                    <tr
                      key={a.name}
                      className="border-b border-slate-800/50 text-slate-200"
                    >
                      <td className="py-2 pr-4">{a.name}</td>
                      <td className="py-2 pr-4">
                        <Badge color={statusColor(a.status)} size="xs">
                          {a.status}
                        </Badge>
                      </td>
                      <td className="py-2 pr-4">{fmtInt(a.reach)}</td>
                      <td className="py-2 pr-4">
                        <span className="font-medium">{fmtPct(a.impact)}</span>{' '}
                        <span className="text-xs text-slate-400">
                          {a.impact_label}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </section>

      {/* Linha 5: Funil de edição (revisão) */}
      <section className="space-y-2">
        <Text className="text-xs uppercase tracking-wide text-slate-400">
          Funil de edição (revisão de verbas)
        </Text>
        <Grid numItems={1} numItemsLg={3} className="gap-4">
          <Card className="lg:col-span-2">
            {isLoading && <Text>carregando…</Text>}
            {!isLoading && errMsg && (
              <div className="text-sm text-red-400" role="alert">
                {errMsg}
              </div>
            )}
            {!isLoading && !errMsg && (
              <>
                <div className="mb-2 flex items-center justify-between">
                  <Text className="text-xs uppercase tracking-wide text-slate-400">
                    passo
                  </Text>
                  <Text className="text-xs uppercase tracking-wide text-slate-400">
                    {fmtInt(ef?.processos_em_revisao)} processos em revisão
                  </Text>
                </div>
                {stepItems.length > 0 ? (
                  <BarList
                    data={stepItems}
                    valueFormatter={(v: number) =>
                      new Intl.NumberFormat('pt-BR').format(v)
                    }
                    color="indigo"
                  />
                ) : (
                  <Text className="text-sm text-slate-400">sem passos</Text>
                )}
              </>
            )}
          </Card>

          <Card className="border border-red-700/40 bg-red-900/10">
            {isLoading && <Text>carregando…</Text>}
            {!isLoading && errMsg && (
              <div className="text-sm text-red-400" role="alert">
                {errMsg}
              </div>
            )}
            {!isLoading && !errMsg && (
              <div className="space-y-1">
                <Text className="text-red-200">Abandono na edição</Text>
                <Metric className="text-red-300">
                  {fmtPct(ef?.taxa_abandono_edicao)}
                </Metric>
                <Text className="pt-1 text-sm text-red-100/80">
                  🔴 {fmtPct(ef?.taxa_abandono_edicao)} dos que editam abandonam
                  sem resultado.
                </Text>
                <Text className="text-xs text-slate-400">
                  {fmtInt(ef?.editores)} editores · {fmtInt(ef?.editou_e_abandonou)}{' '}
                  editaram e abandonaram
                </Text>
              </div>
            )}
          </Card>
        </Grid>
      </section>

      {/* Engajamento por email (tracking Resend) — 5 widgets */}
      <EmailEngagementSection />
    </div>
  );
}
